// /campus/profile — campus_admin edits their school's public profile
// (logo URL, website, short description, career-services contacts,
// brand color). School name + email domains are Coravu-managed and
// rendered read-only. Writes go through the update_my_university_profile
// RPC which enforces scope.

import { useFocusEffect } from "expo-router";
import { COLORS } from "../../../lib/brand";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import {
  getMyUniversityProfile,
  updateMyUniversityProfile,
  type UniversityProfile,
} from "../../../lib/campus";

const BLACK = COLORS.BLACK.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.SAGE.hex;
const LINEN = COLORS.LINEN.hex;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = COLORS.RED_ERROR.hex;
const BORDER = "rgba(255,255,255,0.07)";

export default function CampusProfileScreen() {
  const [profile, setProfile] = useState<UniversityProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [logo, setLogo] = useState("");
  const [website, setWebsite] = useState("");
  const [desc, setDesc] = useState("");
  const [csEmail, setCsEmail] = useState("");
  const [csPhone, setCsPhone] = useState("");
  const [color, setColor] = useState("");
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const p = await getMyUniversityProfile();
      setProfile(p);
      if (p) {
        setLogo(p.logo_url ?? "");
        setWebsite(p.website_url ?? "");
        setDesc(p.short_description ?? "");
        setCsEmail(p.career_services_email ?? "");
        setCsPhone(p.career_services_phone ?? "");
        setColor(p.primary_color_hex ?? "");
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  // Auto-dismiss "Saved" toast after 4 seconds.
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 4000);
    return () => clearTimeout(t);
  }, [savedAt]);

  async function onSave() {
    if (saving) return;
    if (color && !/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{1,5})?$/.test(color)) {
      setErr("Brand color must be a hex like #B5873A or #FF5C8D.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await updateMyUniversityProfile({
        logo_url: logo.trim() || null,
        website_url: website.trim() || null,
        short_description: desc.trim() || null,
        career_services_email: csEmail.trim() || null,
        career_services_phone: csPhone.trim() || null,
        primary_color_hex: color.trim() || null,
      });
      setSavedAt(new Date());
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !profile) {
    return (
      <View style={{ padding: 24 }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  return (
    <View style={{ padding: 24, maxWidth: 760 }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: AMBER }}>
        Campus · School profile
      </Text>
      <Text style={{ marginTop: 6, fontFamily: "CormorantGaramond_300Light", fontSize: 28, lineHeight: 32, color: LINEN }}>
        {profile.name}
      </Text>
      <Text style={{ marginTop: 4, fontFamily: "DMSans_300Light", fontSize: 13, color: WM }}>
        Edit how your school shows up across Coravu. School name and verified email domains are managed by Coravu.
      </Text>

      {err ? <ErrorBanner>{err}</ErrorBanner> : null}
      {savedAt ? (
        <View style={{ marginTop: 14, padding: 10, borderRadius: 4, borderWidth: 1, borderColor: "rgba(143,187,138,0.40)", backgroundColor: "rgba(143,187,138,0.08)" }}>
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: SAGE }}>
            Saved at {savedAt.toLocaleTimeString()}.
          </Text>
        </View>
      ) : null}

      {/* Preview block — shows how the school's profile looks in the shell banner */}
      <View
        style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 6,
          backgroundColor: FOREST_MID,
          borderWidth: 1,
          borderColor: BORDER,
          flexDirection: "row",
          alignItems: "center",
          gap: 14,
        }}
      >
        {logo ? (
          <Image
            source={{ uri: logo }}
            style={{ width: 56, height: 56, borderRadius: 6, backgroundColor: "rgba(255,255,255,0.04)" }}
            resizeMode="contain"
          />
        ) : (
          <View
            style={{
              width: 56,
              height: 56,
              borderRadius: 6,
              backgroundColor: color && color.length > 0 ? color : "rgba(181,135,58,0.16)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 22, color: LINEN }}>
              {profile.name.charAt(0)}
            </Text>
          </View>
        )}
        <View style={{ flex: 1 }}>
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 14, color: LINEN }}>{profile.name}</Text>
          <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11.5, color: WS }} numberOfLines={2}>
            {desc || "Add a short description so students + clients know who you are."}
          </Text>
        </View>
      </View>

      {/* Editable fields */}
      <Section label="Branding">
        <Field label="Logo URL" value={logo} onChange={setLogo} placeholder="https://…/logo.png" />
        <Field
          label="Primary brand color (hex)"
          value={color}
          onChange={(v) => setColor(v.startsWith("#") ? v : v ? `#${v}` : "")}
          placeholder={COLORS.AMBER.hex}
          mono
          helper="Used as the accent color across your campus portal. Leave blank for the default."
        />
      </Section>

      <Section label="About">
        <Field
          label="Website URL"
          value={website}
          onChange={setWebsite}
          placeholder="https://example.edu"
        />
        <Field
          label="Short description"
          value={desc}
          onChange={setDesc}
          placeholder="One or two sentences — what makes your students unique?"
          multiline
        />
      </Section>

      <Section label="Career services contact">
        <Field
          label="Email"
          value={csEmail}
          onChange={setCsEmail}
          placeholder="career-services@example.edu"
        />
        <Field
          label="Phone"
          value={csPhone}
          onChange={setCsPhone}
          placeholder="(555) 123-4567"
        />
      </Section>

      <Section label="Managed by Coravu">
        <ReadField label="School name" value={profile.name} />
        <ReadField
          label="Primary email domain"
          value={profile.primary_email_domain ? `@${profile.primary_email_domain}` : "—"}
        />
        {profile.alt_email_domains.length > 0 ? (
          <ReadField
            label="Alternate email domains"
            value={profile.alt_email_domains.map((d) => `@${d}`).join(", ")}
          />
        ) : null}
        <ReadField label="Active on Coravu" value={profile.is_active ? "Yes" : "No"} />
      </Section>

      {/* Save bar */}
      <View style={{ marginTop: 22, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        <Pressable
          onPress={() => void onSave()}
          disabled={saving}
          style={[
            {
              paddingHorizontal: 16,
              paddingVertical: 11,
              borderRadius: 3,
              backgroundColor: AMBER,
              opacity: saving ? 0.5 : 1,
            } as ViewStyle,
            Platform.OS === "web" && !saving ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 11.5, letterSpacing: 1.4, textTransform: "uppercase", color: COLORS.FOREST.hex }}>
            {saving ? "Saving…" : "Save changes"}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => void load()}
          disabled={saving}
          style={[
            { paddingHorizontal: 14, paddingVertical: 11, borderRadius: 3, borderWidth: 1, borderColor: BORDER, opacity: saving ? 0.5 : 1 } as ViewStyle,
            Platform.OS === "web" && !saving ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: WS }}>Discard</Text>
        </Pressable>
      </View>

      {profile.profile_updated_at ? (
        <Text style={{ marginTop: 12, fontFamily: "DMSans_400Regular", fontSize: 11, color: WL }}>
          Last updated {new Date(profile.profile_updated_at).toLocaleString()}.
        </Text>
      ) : null}
    </View>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 18 }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM, marginBottom: 8 }}>
        {label}
      </Text>
      {children}
    </View>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  multiline,
  mono,
  helper,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  mono?: boolean;
  helper?: string;
}) {
  return (
    <View style={{ marginTop: 10 }}>
      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: WM, marginBottom: 4 }}>
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={WL}
        multiline={multiline}
        autoCapitalize={mono ? "characters" : "none"}
        style={
          {
            minHeight: multiline ? 80 : undefined,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            paddingHorizontal: 12,
            paddingVertical: 9,
            color: W,
            fontFamily: mono ? "SpaceMono" : "DMSans_400Regular",
            fontSize: 13,
            letterSpacing: mono ? 1.2 : 0,
            ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
          } as unknown as TextStyle
        }
      />
      {helper ? (
        <Text style={{ marginTop: 4, fontFamily: "DMSans_400Regular", fontSize: 11, color: WL }}>{helper}</Text>
      ) : null}
    </View>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ marginTop: 8, flexDirection: "row", gap: 8 }}>
      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, letterSpacing: 0.6, color: WM, minWidth: 160 }}>
        {label}
      </Text>
      <Text style={{ flex: 1, fontFamily: "DMSans_400Regular", fontSize: 12, color: WS }}>{value}</Text>
    </View>
  );
}

function ErrorBanner({ children }: { children: string }) {
  return (
    <View style={{ marginTop: 12, padding: 10, borderRadius: 4, borderWidth: 1, borderColor: "rgba(220,80,80,0.40)", backgroundColor: "rgba(220,80,80,0.08)" }}>
      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: RED }}>{children}</Text>
    </View>
  );
}

// Suppress unused-var warning for BLACK; kept in scope as the canonical
// page background even though the rendered surface uses transparent View
// (the shell paints BLACK behind everything).
void BLACK;
